import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

import {
  Shipment,
  ShipmentEvent,
  ShipmentEventType,
  ShipmentMilestoneEvent,
  ShipmentStop,
} from '@/model';

export interface UseShipmentStopsResponse {
  firstUnresolvedDropOffMilestone: ComputedRef<
    ShipmentMilestoneEvent | undefined
  >;
  firstUnresolvedDropOffStop: ComputedRef<ShipmentStop | undefined>;
  firstUnresolvedMilestone: ComputedRef<ShipmentMilestoneEvent | undefined>;
  firstUnresolvedPickupMilestone: ComputedRef<
    ShipmentMilestoneEvent | undefined
  >;
  firstUnresolvedPickupStop: ComputedRef<ShipmentStop | undefined>;
  firstUnresolvedStop: ComputedRef<ShipmentStop | undefined>;
  lastResolvedDropOffMilestone: ComputedRef<ShipmentMilestoneEvent | undefined>;
  lastResolvedDropOffStop: ComputedRef<ShipmentStop | undefined>;
  lastResolvedPickupMilestone: ComputedRef<ShipmentMilestoneEvent | undefined>;
  lastResolvedPickupStop: ComputedRef<ShipmentStop | undefined>;
  lastResolvedMilestone: ComputedRef<ShipmentMilestoneEvent | undefined>;
  lastResolvedStop: ComputedRef<ShipmentStop | undefined>;
  resolvedMilestones: ComputedRef<ShipmentMilestoneEvent[]>;
  resolvedStops: ComputedRef<ShipmentStop[]>;
  shipmentStops: ComputedRef<ShipmentStop[]>;
  unresolvedMilestones: ComputedRef<ShipmentMilestoneEvent[]>;
  unresolvedStops: ComputedRef<ShipmentStop[]>;
}

const milestoneComparator = (
  a: ShipmentMilestoneEvent,
  b: ShipmentMilestoneEvent,
) => {
  const aSequenceNumber = a.relatedStop?.sequenceNumber!;
  const bSequenceNumber = b.relatedStop?.sequenceNumber!;

  if (aSequenceNumber < bSequenceNumber) {
    return -1;
  } else if (aSequenceNumber > bSequenceNumber) {
    return 1;
  } else {
    return 0;
  }
};

export const useShipmentStops = (
  shipment: MaybeRefOrGetter<Shipment>,
  events: MaybeRefOrGetter<ShipmentEvent[]>,
): UseShipmentStopsResponse => {
  const requireBothDates = computed(() => {
    const rawShipment = toValue(shipment);
    return rawShipment.requireBothArrivalAndDepartureOnStops;
  });

  const stops = computed(() => {
    const rawShipment = toValue(shipment);
    return rawShipment.stops;
  });

  const resolvedStops = computed(() =>
    stops.value.filter((s) => s.isResolved(requireBothDates.value)),
  );

  const unresolvedStops = computed(() =>
    stops.value.filter((s) => !s.isResolved(requireBothDates.value)),
  );

  const resolvedMilestones = computed(() => {
    const rawEvents = toValue(events);
    return rawEvents
      .filter((e) => e.isMilestone)
      .map((e) => e as ShipmentMilestoneEvent)
      .filter((milestone) => {
        const relatedStop = stops.value.find(
          (s) => s.id === milestone.relatedStop?.id,
        );

        return relatedStop?.isResolved(requireBothDates.value) ?? false;
      })
      .sort(milestoneComparator);
  });

  const unresolvedMilestones = computed(() => {
    const rawEvents = toValue(events);
    return rawEvents
      .filter((e) => e.isMilestone)
      .map((e) => e as ShipmentMilestoneEvent)
      .filter((milestone) => {
        const relatedStop = stops.value.find(
          (s) => s.id === milestone.relatedStop?.id,
        );

        return relatedStop
          ? !relatedStop.isResolved(requireBothDates.value)
          : false;
      })
      .sort(milestoneComparator);
  });

  const firstUnresolvedStop = computed(() => unresolvedStops.value[0]);

  const firstUnresolvedPickupStop = computed(
    () => unresolvedStops.value.filter((s) => s.isPickup)[0],
  );

  const firstUnresolvedDropOffStop = computed(
    () => unresolvedStops.value.filter((s) => s.isDropOff)[0],
  );

  const firstUnresolvedMilestone = computed(() => {
    return unresolvedMilestones.value.find(
      (e) => e.relatedStopId === firstUnresolvedStop.value?.id,
    );
  });

  const firstUnresolvedPickupMilestone = computed(() => {
    return unresolvedMilestones.value.find(
      (e) => e.eventType === ShipmentEventType.CARRIER_PICKED_UP,
    );
  });

  const firstUnresolvedDropOffMilestone = computed(() => {
    return unresolvedMilestones.value.find(
      (e) => e.eventType === ShipmentEventType.DROPOFF,
    );
  });

  const lastResolvedStop = computed(() => {
    const numResolvedStops = resolvedStops.value.length;
    return resolvedStops.value[numResolvedStops - 1];
  });

  const lastResolvedPickupStop = computed(() => {
    const resolvedPickups = resolvedStops.value.filter((s) => s.isPickup);
    const numResolvedPickups = resolvedPickups.length;
    return resolvedPickups[numResolvedPickups - 1];
  });

  const lastResolvedDropOffStop = computed(() => {
    const resolvedDropOffs = resolvedStops.value.filter((s) => s.isDropOff);
    const numResolvedDropOffs = resolvedDropOffs.length;
    return resolvedDropOffs[numResolvedDropOffs - 1];
  });

  const lastResolvedMilestone = computed(() => {
    const numResolvedMilestones = resolvedMilestones.value.length;
    return resolvedMilestones.value[numResolvedMilestones - 1];
  });

  const lastResolvedPickupMilestone = computed(() => {
    return resolvedMilestones.value.findLast(
      (e) => e.eventType === ShipmentEventType.CARRIER_PICKED_UP,
    );
  });

  const lastResolvedDropOffMilestone = computed(() => {
    return resolvedMilestones.value.findLast(
      (e) => e.eventType === ShipmentEventType.DROPOFF,
    );
  });

  return {
    firstUnresolvedDropOffMilestone,
    firstUnresolvedDropOffStop,
    firstUnresolvedMilestone,
    firstUnresolvedPickupMilestone,
    firstUnresolvedPickupStop,
    firstUnresolvedStop,
    lastResolvedDropOffMilestone,
    lastResolvedDropOffStop,
    lastResolvedMilestone,
    lastResolvedStop,
    lastResolvedPickupMilestone,
    lastResolvedPickupStop,
    resolvedMilestones,
    resolvedStops,
    shipmentStops: stops,
    unresolvedMilestones,
    unresolvedStops,
  };
};
